/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import {logError, logWarning} from "App/debug";
import {vec2, vec3} from "gl-matrix";

/**
 * @param {Element} element
 * @param {string} event
 * @param {function} funct
 */

export function addEvent(element: any, event: string, funct: any) {
    if (element.attachEvent) {
        return element.attachEvent('on' + event, funct);
    }
    return element.addEventListener(event, funct, false);
};

/**
 * Dynamically load script and when is ready, call callback function.
 *
 * @param {string} url
 * @param {function} [cbSuccess = undefined]
 * @param {function} [cbError = undefined]
 * @param {object} [cbData = undefined]
 */
export function loadScript(url: string, cbSuccess: any, cbError: any, cbData: object) {
    let head = document.getElementsByTagName('head')[0];
    let script = document.createElement('script');

    let callbackSuccess = function () {
        if (cbSuccess) {
            cbSuccess(cbData);
        }
    };

    let callbackError = function () {
        logError("Cannot load javascript from '" + url + "'");
        if (cbError) {
            cbError(cbData);
        }
    };

    let callbackStateChange = function () {
        // TODO Detect failed and success event
        logWarning("loadScript[onReadyStateChange]: Detection if javascript file is loaded or not is not implemented here. I suppose it is loaded now!");

        if (cbSuccess) {
            cbSuccess(cbData);
        }
    };

    // Add events
    addEvent(script, 'load', callbackSuccess);
    addEvent(script, 'error', callbackError);
    addEvent(script, 'onreadystatechange', callbackStateChange);

    // Load
    script.type = 'text/javascript';
    script.src = url;
    head.appendChild(script);
}

export function getBytesPerElementFromType(aType: string): any {
    switch (aType) {
        case "Int8":
            return Int8Array.BYTES_PER_ELEMENT;
        case "Uint8":
            return Uint8Array.BYTES_PER_ELEMENT;
        case "Int16":
            return Int16Array.BYTES_PER_ELEMENT;
        case "Uint16":
            return Uint16Array.BYTES_PER_ELEMENT;
        case "Int32":
            return Int32Array.BYTES_PER_ELEMENT;
        case "Uint32":
            return Uint32Array.BYTES_PER_ELEMENT;
        case "Float32":
            return Float32Array.BYTES_PER_ELEMENT;
        case "Float64":
            return Float64Array.BYTES_PER_ELEMENT;
        default:
            logError("Unknown type " + aType + ", trying Float32...");
            return Float32Array.BYTES_PER_ELEMENT;
    }
}

export function checkGLError(gl: WebGLRenderingContext): number {
    // TODO: handle the errors checking in some time constrained loops?
    let glErr = 0;// gl.getError(); // commented out becouse it slows down model loading too much
    if (glErr) {
        if (glErr === gl.OUT_OF_MEMORY) {
            logError("GL-Error " + glErr + " (out of memory).");
        } else {
            logError("GL-Error " + glErr);
        }
    }

    return glErr;
}

export function generate3DLine(aData: any, aGeneratedData: any): void {
    let CDWK_TESSELATION_FACTOR = 4;

    // pass through indexes of input line and generate geometry for line segments
    let lines = {};
    let lineCnt = 0;
    lines[lineCnt] = [];
    let i;
    let line;
    for (i = 0; i < aData.indices.length; i += 2) {
        line = lines[lineCnt];
        line.push(aData.indices[i]);

        if (aData.indices[i + 1] !== aData.indices[i + 2]) {
            line.push(aData.indices[i + 1]);
            if (i < (aData.indices.length - 2)) {
                lineCnt++;
                lines[lineCnt] = [];
            }
        }
    }

    let angles = [];
    for (let a = 0; a < CDWK_TESSELATION_FACTOR; a++) {
        angles.push(a * (2 * Math.PI / CDWK_TESSELATION_FACTOR));
    }

    let offset = 0;
    let prev;
    let next;
    for (let key in lines) {

        let line = lines[key];

        // create vertex property (i.e. vertexes and normals)
        let j = 0;
        let a, b;
        let distanceFromOrigin = 0;

        let normal = vec3.create();
        let scaledDir0 = vec3.create();
        let scaledDir1 = vec3.create();

        let dir = vec3.create();
        let dir0 = vec3.create();
        let dir1 = vec3.create();
        let pos0 = vec3.create();
        let pos1 = vec3.create();

        let upDir = vec3.fromValues(0, 1, 0);
        let leftDir = vec3.fromValues(1, 0, 0);
        let positions: vec3[] = [vec3.create(), vec3.create()];

        for (i = 0; i < line.length - 1; i++) {
            vec3.set(pos0, aData.positions[line[i] * 3 + 0], aData.positions[line[i] * 3 + 1], aData.positions[line[i] * 3 + 2]);
            vec3.set(pos1, aData.positions[line[i + 1] * 3 + 0], aData.positions[line[i + 1] * 3 + 1], aData.positions[line[i + 1] * 3 + 2]);

            positions[0] = pos0;
            positions[1] = pos1;

            for (b = 0; b < 2; b++) {
                prev = (b > 0) ? b - 1 : 0;
                next = (b < 1) ? b + 1 : 1;

                // compute normalized direction
                vec3.subtract(dir, positions[next], positions[prev]);
                vec3.normalize(dir, dir);

                vec3.cross(dir0, dir, upDir);
                vec3.normalize(dir0, dir0);

                if (vec3.length(dir0) < 0.0001) {
                    vec3.cross(dir0, dir, leftDir);
                    vec3.normalize(dir0, dir0);
                }

                vec3.cross(dir1, dir0, dir);
                vec3.normalize(dir1, dir1);

                for (a = 0; a < CDWK_TESSELATION_FACTOR; a++) {

                    vec3.scale(scaledDir0, dir0, Math.cos(angles[a]));
                    vec3.scale(scaledDir1, dir1, Math.sin(angles[a]));
                    vec3.add(normal, scaledDir0, scaledDir1);

                    aGeneratedData.positions.push(positions[b][0]);
                    aGeneratedData.positions.push(positions[b][1]);
                    aGeneratedData.positions.push(positions[b][2]);

                    aGeneratedData.normals.push(normal[0]);
                    aGeneratedData.normals.push(normal[1]);
                    aGeneratedData.normals.push(normal[2]);

                    aGeneratedData.distances.push(distanceFromOrigin);

                    j++;
                }
                distanceFromOrigin += vec3.distance(positions[0], positions[1]);
            }

            b = offset;
            let bCdwkTf = b * (CDWK_TESSELATION_FACTOR);
            let bCdwkTfP1 = (b + 1) * (CDWK_TESSELATION_FACTOR);
            for (a = 0; a < CDWK_TESSELATION_FACTOR; a++) {
                if (a === (CDWK_TESSELATION_FACTOR - 1)) { // stitch the shape end with beginning
                    aGeneratedData.indices.push(bCdwkTf + a);
                    aGeneratedData.indices.push(bCdwkTfP1 + a);
                    aGeneratedData.indices.push(bCdwkTf + a + 1);

                    aGeneratedData.indices.push(bCdwkTf + a);
                    aGeneratedData.indices.push(bCdwkTf + a + 1);
                    aGeneratedData.indices.push(bCdwkTf);
                } else {
                    aGeneratedData.indices.push(bCdwkTf + a);
                    aGeneratedData.indices.push(bCdwkTfP1 + a);
                    aGeneratedData.indices.push(bCdwkTfP1 + a + 1);

                    aGeneratedData.indices.push(bCdwkTf + a);
                    aGeneratedData.indices.push(bCdwkTfP1 + a + 1);
                    aGeneratedData.indices.push(bCdwkTf + a + 1);
                }
            }
            offset += 2;
        } // single line
    } // lines
}

export function Base64ToUint8Array(aBase64String: string): Uint8Array {
    let array = new Uint8Array(window.atob(aBase64String).split("").map(function (c) {
        return c.charCodeAt(0);
    }));
    return array;
}

export function checkIE11OrAndroidAndFileLocation(): boolean {
    // @ts-ignore
    let isIE11 = !(window.ActiveXObject) && "ActiveXObject" in window;
    let isAndroid = (document.location.protocol.indexOf("content") !== -1);

    // the content is for Andorid local files on which since cca 20.2.2018 it is prohibited probably to create workers from blob so the page crashes
    let isFileLocation = document.location.protocol !== "" && (document.location.protocol.indexOf("file") !== -1 || document.location.protocol.indexOf("content") !== -1);

    return (isIE11 || isAndroid) && isFileLocation;
}

export function hexToRgbf(hex: number): number[] {
    let r = (hex >> 16) & 0xFF;
    let g = (hex >> 8) & 0xFF;
    let b = hex & 0xFF;
    return [r / 255.0, g / 255.0, b / 255.0];
}

export function hexStrToRgb(hex: string): number[] {
    let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (result) {
        return [
            parseInt(result[1], 16),
            parseInt(result[2], 16),
            parseInt(result[3], 16)
        ]
    }
    else {
        return null;
    }
}

export function mergeBuffers(buffer1: ArrayBuffer, buffer2: ArrayBuffer): Uint8Array {
    let tmp = new Uint8Array(buffer1.byteLength + buffer2.byteLength);
    tmp.set(new Uint8Array(buffer1), 0);
    tmp.set(new Uint8Array(buffer2), buffer1.byteLength);
    return tmp;
}
